import React, { useState, useEffect } from 'react';
import { Datepicker, START_DATE, END_DATE } from '@datepicker-react/styled';
import { ThemeProvider } from 'styled-components';
import { Button } from '@material-ui/core';
import Popover from '@material-ui/core/Popover';
import moment from 'moment';
import useTranslation from 'Hooks/useTranslation';
import { useAppSettings } from 'Contexts/AppSettingsProvider';

const CalendarIcon = () => <div className="calendarIcon" />;

const SettingsDateRangePicker = ({
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    onChange,
    onReset,
    handleClosePopover,
    handleReset,
}) => {
    const translation = useTranslation();
    const { appTheme: theme } = useAppSettings();
    const [anchorEl, setAnchorEl] = useState(null);
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null,
        focusedInput: START_DATE,
    });

    useEffect(() => {
        if (!dateFrom && !dateTo) {
            setDateRange({ startDate: null, endDate: null, focusedInput: START_DATE });
        } else if (dateFrom && dateTo) {
            setDateRange({
                startDate: new Date(dateFrom),
                endDate: new Date(dateTo),
                focusedInput: END_DATE,
            });
        }
    }, [dateFrom, dateTo]);

    const handleDatesChange = (data) => {
        setDateRange((prev) => ({
            ...data,
            focusedInput: data.focusedInput ?? prev.focusedInput,
        }));
    };

    const closeMenu = () => {
        let adjustedStartDate = null;
        let adjustedEndDate = null;

        if (dateRange.startDate && (!dateRange.endDate || dateRange.startDate.getTime() === dateRange.endDate.getTime())) {
            adjustedStartDate = new Date(dateRange.startDate);
            adjustedStartDate.setHours(0, 0, 0, 0);
            adjustedEndDate = new Date(dateRange.startDate);
            adjustedEndDate.setHours(23, 59, 59, 999);
        } else if (dateRange.startDate && dateRange.endDate) {
            adjustedStartDate = new Date(dateRange.startDate);
            adjustedStartDate.setHours(0, 0, 0, 0);
            adjustedEndDate = new Date(dateRange.endDate);
            adjustedEndDate.setHours(23, 59, 59, 999);
        }

        if (adjustedStartDate && adjustedEndDate) {
            const fromIso = adjustedStartDate.toISOString();
            const toIso = adjustedEndDate.toISOString();
            const name = `${moment(adjustedStartDate).format('DD-MMM-YY')} to ${moment(adjustedEndDate).format('DD-MMM-YY')}`;
            setDateFrom(fromIso);
            setDateTo(toIso);
            onChange({
                dateType: 'customRange',
                name,
                dateFrom: adjustedStartDate,
                dateTo: adjustedEndDate,
            });
        } else if (handleReset) {
            handleReset();
        }

        setAnchorEl(null);
        if (handleClosePopover) handleClosePopover();
    };

    const reset = () => {
        setDateRange({ startDate: null, endDate: null, focusedInput: START_DATE });
        setDateFrom(null);
        setDateTo(null);
        if (onReset) onReset();
    };

    return (
        <div className="tableDateFilterHolder">
            <Button
                onClick={(e) => setAnchorEl(e.currentTarget)}
                startIcon={<CalendarIcon />}
                className="customDateRangeBtn"
            >
                Select Custom Range
            </Button>
            <Popover
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={closeMenu}
            >
                <div className="popoverCalendarHolder">
                    <div className="calendarHolder">
                        <ThemeProvider
                            theme={{
                                reactDatepicker: {
                                    daySize: [36, 40],
                                    fontFamily: '"Open Sans", sans-serif',
                                    colors: {
                                        accessibility: 'transparent',
                                        selectedDay: theme === 'Light' ? 'rgba(68, 68, 68, 0.05)!important' : 'rgba(255, 255, 255, 0.05)!important',
                                        selectedDayHover: theme === 'Light' ? 'rgba(68, 68, 68, 0.05)!important' : 'rgba(255, 255, 255, 0.05)!important',
                                        primaryColor: theme === 'Light' ? 'rgba(68, 68, 68, 0.12)!important' : 'rgba(255, 255, 255, 0.12)!important',
                                    },
                                },
                            }}
                        >
                            <Datepicker
                                onDatesChange={handleDatesChange}
                                startDate={dateRange.startDate}
                                endDate={dateRange.endDate}
                                focusedInput={dateRange.focusedInput}
                                showResetDates={false}
                                showSelectedDates={false}
                                showClose={false}
                            />
                        </ThemeProvider>
                    </div>
                    <div className="btnHolder justified fixedWidthBtn mt32">
                        <Button variant="outlined" onClick={reset}>
                            {translation.resetButtonLabel}
                        </Button>
                        <Button variant="contained" color="primary" onClick={closeMenu}>
                            {translation.applyButtonLabel}
                        </Button>
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export default SettingsDateRangePicker;
